/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/

import React, { memo, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { Stack } from '@mui/material';

import { ClusterStateOrder } from 'shared';

import { IUser } from '../../../types/user/IUser';
import { fetchClusters } from '../../../utils/queryFns';
import { useFavoriteServices } from '../../../utils/useFavoriteServices';
import { useProjectPermissions } from '../../../utils/useProjectPermissions';
import { FilterPanel } from './FilterPanel/FilterPanel';
import { IOption } from './ProjectPage.types';
import { ServiceCard } from './ServiceCard';
import { ServiceCardNew } from './ServiceCardNew/ServiceCardNew';

type Props = {
  organizationId: string;
  projectId: string;
  user: IUser;
};

export const ServiceList = memo((props: Props) => {
  const { organizationId, projectId } = props;

  const permissions = useProjectPermissions(organizationId, projectId);

  const [search, setSearch] = useState<string>('');
  const [selectType, setSelectType] = useState<IOption | null>(null);
  const [selectInstance, setSelectInstance] = useState<IOption | null>(null);
  const [selectInstanceOptions, setSelectInstanceOptions] = useState<IOption[]>([]);
  const [selectStatus, setSelectStatus] = useState<IOption | null>(null);

  const { favoriteServices, toggleFavorite } = useFavoriteServices();

  const servicesQuery = useQuery([fetchClusters.key, { organizationId, projectId }], fetchClusters.fn);
  const unfilteredServices = useMemo(() => servicesQuery?.data?.payload ?? [], [servicesQuery]);
  const services = useMemo(
    () =>
      unfilteredServices
        .map(service => ({
          ...service,
          isFavorite: favoriteServices.some(fv => fv.id === service.id),
        }))
        .filter(service => (selectType ? service.type === selectType.value : true))
        .filter(service =>
          search.length ? service.name.toLowerCase().indexOf(search.toLowerCase().trim()) >= 0 : true,
        )
        .filter(service => (selectStatus ? service.status === selectStatus.value : true))
        .filter(service =>
          selectInstance
            ? (service.configuration?.find(conf => conf.endedAt === 'infinity')?.replicas ?? 0) + 1 ===
              selectInstance.value
            : true,
        )
        .sort(
          (a, b) =>
            Number(b.isFavorite) - Number(a.isFavorite) ||
            (ClusterStateOrder[a.status!] || 99) - (ClusterStateOrder[b.status!] || 99),
        ),
    [search, selectInstance, selectStatus, selectType, unfilteredServices, favoriteServices],
  );

  useEffect(() => {
    const instances: number[] = [];

    unfilteredServices.forEach(service => {
      const replicas = service.configuration?.find(conf => conf.endedAt === 'infinity')?.replicas ?? 0;
      instances.push(replicas + 1);
    });

    const uniqInstances = instances.filter((val, idx, self) => self.indexOf(val) === idx).map(val => ({ value: val }));

    setSelectInstanceOptions(uniqInstances);

    return () => {
      setSearch('');
      setSelectType(null);
      setSelectStatus(null);
      setSelectInstance(null);
    };
  }, [unfilteredServices]);

  const onChangeSelect = (name: string, option: IOption | null) => {
    switch (name) {
      case 'type':
        setSelectType(option);
        break;
      case 'status':
        setSelectStatus(option);
        break;
      case 'instance':
        setSelectInstance(option);
        break;
    }
  };

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  return (
    <Stack gap={2}>
      <FilterPanel
        isLoading={servicesQuery.isLoading}
        search={search}
        type={selectType}
        status={selectStatus}
        instance={selectInstance}
        selectInstanceOptions={selectInstanceOptions}
        onChangeSelect={onChangeSelect}
        onChangeSearch={onSearch}
        disabled={!unfilteredServices.length}
      />

      <Stack direction="row" flexWrap="wrap" gap={3}>
        {services.map(service => (
          <ServiceCard
            favoriteServices={favoriteServices}
            toggleFavorite={toggleFavorite}
            supportPlan={service.configuration?.sort((a, b) => Number(b.id) - Number(a.id))?.[0]?.supportPlan}
            key={service.id}
            serviceId={service.id}
            projectId={projectId}
            organizationId={organizationId}
            k8sNamespace={`${service.namespace}-${service.sequenceId}-${service.id}`}
            name={service.name}
            type={service.type}
            status={service.status}
            instances={(service.configuration?.find(conf => conf.endedAt === 'infinity')?.replicas ?? 0) + 1}
          />
        ))}
        <ServiceCardNew disabled={!permissions.create_cluster} />
      </Stack>
    </Stack>
  );
});
